/**
 * Per-request CSP nonce. nginx swaps `__CSP_NONCE__` in index.html for a fresh value and Vite
 * emits it as <meta property="csp-nonce">. Undefined in `npm start` (no CSP there).
 */
export const getCspNonce = (): string | undefined => {
  if (typeof document === 'undefined') return undefined;
  const meta = document.querySelector<HTMLMetaElement>('meta[property=csp-nonce]');
  // `.nonce` keeps the value; browsers blank the attribute once parsed.
  const nonce = meta?.nonce || meta?.getAttribute('nonce');
  return nonce || undefined;
};

/**
 * Libraries that build their UI with `document.createElement('style')` (MailCraft's shadow-DOM
 * stylesheet) never set a nonce, so the CSP blocks them and the UI renders unstyled. Stamp the
 * nonce on every <style> created this way. Markup injected via innerHTML is still blocked.
 */
export const installStyleNonce = (): void => {
  const nonce = getCspNonce();
  if (!nonce) return;
  const createElement = document.createElement.bind(document);
  document.createElement = ((tagName: string, options?: ElementCreationOptions) => {
    const element = createElement(tagName, options);
    if (element instanceof HTMLStyleElement) element.nonce = nonce;
    return element;
  }) as typeof document.createElement;
};

/**
 * srcdoc iframes inherit the page CSP, which would drop every <style> block in an email preview.
 * Stamp the nonce on them. Safe because all preview iframes are sandboxed without allow-scripts.
 */
export const withStyleNonce = (html: string): string => {
  const nonce = getCspNonce();
  if (!nonce) return html;
  return html.replace(/<style\b(?![^>]*\snonce=)/gi, `<style nonce="${nonce}"`);
};
